import _ from 'lodash';
import React, {FC, useState} from 'react';
import {connect} from 'react-redux';

import {useInitialEffect} from '../../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../../srcCommon/helpers/reselectHelpers';
import {addAccount} from '../../../helpers/models/cardAttributeUpdateHelpers';
import {globalNamespace} from '../../../helpers/models/namespaceHelpers';
import {ResolveDispatchProps} from '../../../helpers/reduxHelpers';
import {CardModel} from '../../../models/api/cardModel';
import {CrmSyncCardDefaultFieldIdsEnum} from '../../../models/api/crmSyncModel';
import {VisualContactModel} from '../../../models/api/visualContactModel';
import {AppState} from '../../../store/appState';
import {ThunkAction, ThunkAsyncAction} from '../../../store/appThunk';
import {
  fetchAccountAsyncAction,
  fetchAllAccountsAsyncAction,
} from '../../../store/data/accounts/accountsActions';
import {makeGetAccount} from '../../../store/data/accounts/accountsSelectors';
import {updateCardAttributeAsyncAction} from '../../../store/data/cards/cardsActions';
import {makeGetCardCrmSyncEditLinkForField} from '../../../store/data/crmSync/crmSyncsSelectors';
import {getAccounts} from '../../../store/data/dataSelectors';
import {popoutAccountDetailsAction} from '../../../store/panels/accounts/accountDetailsPanelActions';
import {createCardAndUpdateContactDetailsPanelAction} from '../../../store/panels/contacts/contactDetailsPanelActions';
import {ContactDetailsPanelId} from '../../../store/panels/panelStateBase';
import {ContactHeaderAccount} from './contactHeaderAccount';

/*
 * Props.
 */

interface ContactHeaderAccountContainerPublicProps {
  card: CardModel | undefined;
  contact?: VisualContactModel;
  panelId: ContactDetailsPanelId;
  popupWidth: number;
}
type ContactHeaderAccountContainerStateProps = ResolveStateProps<typeof mapStateToProps>;
type ContactHeaderAccountContainerDispatchProps = ResolveDispatchProps<typeof mapDispatchToProps>;
type ContactHeaderAccountContainerProps = ContactHeaderAccountContainerPublicProps &
  ContactHeaderAccountContainerDispatchProps &
  ContactHeaderAccountContainerStateProps;

/*
 * Selectors.
 */

function getAccountId(_state: AppState, props: ContactHeaderAccountContainerPublicProps) {
  return props.card?.accountId;
}

function getCard(_state: AppState, props: ContactHeaderAccountContainerPublicProps) {
  return props.card;
}
function getFieldId() {
  return CrmSyncCardDefaultFieldIdsEnum.ACCOUNT_ID;
}

/*
 * Component.
 */

const ContactHeaderAccountContainerContainer: FC<ContactHeaderAccountContainerProps> = (props) => {
  const {
    card,
    account,
    accounts,
    crmSyncEditLink,
    fetchAccountAsync,
    fetchAllAccountsAsync,
    openAccountDetailsPopUp,
    onAddAccountAsync,
  } = props;
  const [shouldHideAddAccountButton, setShouldHideAddAccountButton] = useState(false);

  useInitialEffect(() => {
    if (!account && card?.accountId) {
      fetchAccountAsync(card.accountId);
    }
  });

  useInitialEffect(() => {
    async function maybeFetchAccountsAsync() {
      /**
       * If we do not have any accounts in the redux store and non-added to the card,
       * check if we should be showing accounts button at all. If they do not have any
       * accounts, we should not show the button.
       */
      if (_(accounts).keys().value().length === 0 && !card?.accountId) {
        try {
          const fetchResults = await fetchAllAccountsAsync({limit: 1});
          setShouldHideAddAccountButton(!fetchResults.count || fetchResults.count === 0);
        } catch {
          setShouldHideAddAccountButton(true);
        }
      }
    }
    maybeFetchAccountsAsync();
  });

  if (shouldHideAddAccountButton) {
    return null;
  }

  return (
    <ContactHeaderAccount
      account={account}
      card={card}
      crmSyncEditLink={crmSyncEditLink}
      accounts={accounts}
      openAccountDetailsPopUp={openAccountDetailsPopUp}
      onAddAccountAsync={onAddAccountAsync}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<ContactHeaderAccountContainerPublicProps>()(() => ({
  account: makeGetAccount(getAccountId),
  accounts: getAccounts,
  crmSyncEditLink: makeGetCardCrmSyncEditLinkForField(getFieldId, getCard),
}));

const mapDispatchToProps = makeMapDispatchToProps<ContactHeaderAccountContainerPublicProps>()((props) => ({
  fetchAccountAsync: fetchAccountAsyncAction,
  fetchAllAccountsAsync: fetchAllAccountsAsyncAction,
  onAddAccountAsync:
    (accountId: number): ThunkAsyncAction =>
    async (dispatch) => {
      const {panelId, contact, card} = props;
      const createNewCardAsync = async () => {
        if (!contact) {
          return undefined;
        }
        const createdCard = await dispatch(
          createCardAndUpdateContactDetailsPanelAction(
            panelId,
            {
              namespace: globalNamespace,
              name: contact.name || contact.contact.handle,
              contacts: [contact.contact],
            },
            {shouldHideLoadingState: true},
          ),
        );
        return createdCard;
      };

      const selectedCard = card ?? (await createNewCardAsync());
      if (!selectedCard) {
        return;
      }

      dispatch(
        updateCardAttributeAsyncAction({
          card: selectedCard,
          action: addAccount(accountId),
          shouldShowUndoBanner: true,
          panelId,
        }),
      );
    },
  openAccountDetailsPopUp: (): ThunkAction => (dispatch) => {
    const {card, panelId, popupWidth} = props;
    if (!card?.accountId) {
      return;
    }

    dispatch(popoutAccountDetailsAction(panelId, card.accountId, popupWidth));
  },
}));

export const ContactHeaderAccountContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(ContactHeaderAccountContainerContainer);
